import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus } from 'lucide-react';
import {
  stcMovementPriorityService,
  StcMovementPriorityDto,
  BulkUpdateStcMovementPriorityRequest
} from '../../../../../services/stcMovementPriorityService';
import { stcMovementTypeService, StcMovementTypeDto } from '../../../../../services/stcMovementTypeService';
import { stcBinTypeService, StcBinTypeDto } from '../../../../../services/stcBinTypeService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { MovementCodePrioritySidePanel } from './MovementCodePrioritySidePanel';
import { MovementCodePriorityCreateModal } from './MovementCodePriorityCreateModal';

interface MovementCodePriorityDataTableProps {
  refreshTrigger?: number;
}

export const MovementCodePriorityDataTable: React.FC<MovementCodePriorityDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  // Core data state
  const [data, setData] = useState<StcMovementPriorityDto[]>([]);
  const [movementTypes, setMovementTypes] = useState<StcMovementTypeDto[]>([]);
  const [binTypes, setBinTypes] = useState<StcBinTypeDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Modal state
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Inline editing - uses bulkUpdate to support changing all fields including composite key parts
  const inlineEditConfig = useInlineEdit<StcMovementPriorityDto>({
    getRowId: (row) => row.id,
    onSave: async (originalRow, editedData) => {
      const request: BulkUpdateStcMovementPriorityRequest = {
        companyNo: originalRow.companyNo,
        movementTypeNo: (editedData.movementTypeNo as number) ?? originalRow.movementTypeNo,
        binTypeNo: (editedData.binTypeNo as number) ?? originalRow.binTypeNo,
        fromPriority: (editedData.fromPriority as number) ?? originalRow.fromPriority,
        toPriority: (editedData.toPriority as number) ?? originalRow.toPriority
      };
      await stcMovementPriorityService.bulkUpdate([request]);
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || 'Feil ved lagring')
  });

  // Load data
  useEffect(() => {
    loadData();
    loadMovementTypes();
    loadBinTypes();
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const priorities = await stcMovementPriorityService.getCurrent().catch(() =>
        stcMovementPriorityService.getAll().catch(() => [])
      );
      setData(priorities);
    } catch (err: any) {
      console.error('Error loading movement code priority data:', err);
      setError('Kunne ikke laste bevegelseskoder, prioritet. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [companyNo]);

  const loadMovementTypes = async () => {
    try {
      const types = await stcMovementTypeService.getCurrent().catch(() =>
        stcMovementTypeService.getAll().catch(() => [])
      );
      setMovementTypes(types);
    } catch (err: any) {
      console.error('Error loading movement types:', err);
    }
  };

  const loadBinTypes = async () => {
    try {
      const types = await stcBinTypeService.getCurrent().catch(() =>
        stcBinTypeService.getAll().catch(() => [])
      );
      setBinTypes(types);
    } catch (err: any) {
      console.error('Error loading bin types:', err);
    }
  };

  const getMovementTypeDescription = useCallback((movementTypeNo: number): string => {
    const mt = movementTypes.find(m => m.movementTypeNo === movementTypeNo);
    return mt ? mt.description1 : `Bevegelseskode ${movementTypeNo}`;
  }, [movementTypes]);

  const getBinTypeDescription = useCallback((binTypeNo: number): string => {
    const bt = binTypes.find(b => b.binTypeNo === binTypeNo);
    return bt ? (bt.description1 || `Lokasjonstype ${binTypeNo}`) : `Lokasjonstype ${binTypeNo}`;
  }, [binTypes]);

  // Column factory
  const columnFactory = createColumnFactory<StcMovementPriorityDto>();

  // Build editOptions from master data
  const movementTypeOptions = useMemo(() =>
    movementTypes.map(mt => ({
      value: mt.movementTypeNo,
      label: `${mt.description1} (${mt.movementTypeNo})`
    })),
    [movementTypes]
  );

  const binTypeOptions = useMemo(() =>
    binTypes.map(bt => ({
      value: bt.binTypeNo,
      label: `${bt.description1 || `Lokasjonstype ${bt.binTypeNo}`} (${bt.binTypeNo})`
    })),
    [binTypes]
  );

  // Column definitions - all fields editable
  const columns: ColumnDefinition<StcMovementPriorityDto>[] = useMemo(() => [
    columnFactory.createNumberColumn('movementTypeNo', 'movementTypeNo', 'Bevegelseskode', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true,
      editType: 'select',
      editOptions: movementTypeOptions,
      formatter: (value: any) => {
        if (value == null) return '-';
        return getMovementTypeDescription(value);
      }
    }),

    columnFactory.createNumberColumn('binTypeNo', 'binTypeNo', 'Lokasjonstype', {
      minWidth: 160,
      sortable: true,
      filterType: 'both',
      editable: true,
      editType: 'select',
      editOptions: binTypeOptions,
      formatter: (value: any) => {
        if (value == null) return '-';
        return getBinTypeDescription(value);
      }
    }),

    columnFactory.createNumberColumn('fromPriority', 'fromPriority', 'Fra prioritet', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createNumberColumn('toPriority', 'toPriority', 'Til prioritet', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    })
  ], [getMovementTypeDescription, getBinTypeDescription, movementTypeOptions, binTypeOptions]);

  // Handle bulk edit
  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert('Velg minst én rad for å redigere');
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length]);

  // Table actions
  const tableActions: TableAction<StcMovementPriorityDto>[] = useMemo(() => [
    {
      id: 'create',
      label: 'Ny bevegelseskode, prioritet',
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: `Vedlikehold (${selectedIds.length})`,
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit]);

  // Build selected rows map for SidePanel compatibility
  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'movement-code-priority-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {/* Bulk Edit Side Panel */}
      {showBulkEditModal && selectedIds.length > 0 && (
        <MovementCodePrioritySidePanel
          selectedRows={selectedRowsMap}
          data={data}
          movementTypes={movementTypes}
          binTypes={binTypes}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <MovementCodePriorityCreateModal
          isOpen={showCreateModal}
          movementTypes={movementTypes}
          binTypes={binTypes}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
